import { activeLines, activeTier, tokens } from './blocks.js';
import { collectFields } from './collect.js';
import { decodeEntities } from './htmlParse.js';
import { htmlToTree } from './htmlTree.js';
import { offerVariantSource, unitVariantIds } from './offers.logic.js';
/**
 * The confirmation screen's text: the merchant's shortcodes, and their HTML as markdown.
 *
 * Two jobs:
 *
 *  1. `thankYouTokens` — the full shortcode set the admin documents for the thank-you page and the
 *     WhatsApp message (`settings.thankYouPlaceholders`). `tokens()` in `blocks.ts` covers the three
 *     that any block may use; these are the ones that read the buyer's own answers, the shipping
 *     rate they picked and the order the backend created.
 *
 *     🚨 **Also the WhatsApp BLOCK's message**, which is built before any order exists — the block
 *     shipped interpolating `tokens()` alone, so a merchant's message reached the chat with
 *     `{full_name}`, `{phone}` and `{products_summary_with_quantity}` still in it and only the
 *     total filled in. Everything here resolves against `FormState`, so the only tokens that need
 *     the order are `{order_number}` / `{order_id}`, which are empty until it exists.
 *  2. `thankYouTree` — that interpolated HTML as a component tree (`htmlTree.ts`), which is what
 *     the confirmation screen renders. `thankYouMarkdown` is the fallback behind it, for pages the
 *     converter cannot read.
 *
 * 🚨 **The token semantics are the legacy renderer's, deliberately.** `form-renderer`'s
 * `buildWaMessage` / `interpolateThankYou` (`form.ts:806-880`) is what merchants' existing pages were
 * written against, and both bundles ship. A shortcode that resolves differently on the two paths is
 * a thank-you page that reads correctly for a buyer on one theme and wrongly for a buyer on another.
 *
 * `{page_url}` comes from `ProductContext.pageUrl`, because a worker has no `window.location` to
 * read it from. The legacy `buildWaMessage` interpolates it on BOTH surfaces — the WhatsApp block
 * and the thank-you page — so this does too, even though the admin only documents it for the
 * block. With no `pageUrl` (the admin preview) it is left untouched rather than emptied, so a
 * merchant sees the shortcode and not a blank.
 */
/**
 * A standard field's value, honouring `connect_to`.
 *
 * The direct key wins; otherwise the first custom field wired to that standard key. Same rule as the
 * backend and the legacy `resolveStd`, so `{phone}` still resolves for a merchant who deleted the
 * built-in phone field and connected a custom one.
 */
function resolveStd(o, values, key) {
    const direct = values[key]?.trim();
    if (direct)
        return direct;
    for (const f of o.config.fields) {
        const v = f.connect_to === key ? values[f.key]?.trim() : undefined;
        if (v)
            return v;
    }
    return '';
}
/** The variant labels the buyer picked across an offer's per-unit selectors, one per unit. */
function offerVariantLabels(o) {
    const offer = o.build.offer;
    const tier = activeTier(o);
    if (!offer || !tier)
        return [];
    const labelById = new Map(offerVariantSource(offer, o.ctx).variants.map((v) => [v.id, v.name]));
    return unitVariantIds(tier, o.ctx, o.build.state.tierVariantIds)
        .map((id) => labelById.get(id))
        .filter((label) => Boolean(label));
}
/**
 * `{products_summary_with_quantity}` — "Name (Variant) x N", comma-separated.
 *
 * Three sources in priority order, mirroring the legacy: the cart's own lines, then an offer's
 * per-unit picks grouped by variant (so a 3-unit bundle of two colours reads "… (Azul) x 2, …
 * (Rojo) x 1"), then the single product with the variant the storefront read.
 */
function productsSummary(o) {
    const lines = o.ctx.lineItems?.length ? activeLines(o) : [];
    if (lines.length) {
        return lines
            .map((li) => `${li.name}${li.variant ? ` (${li.variant})` : ''} x ${li.quantity}`)
            .join(', ');
    }
    const labels = offerVariantLabels(o);
    if (labels.length) {
        const counts = new Map();
        for (const label of labels)
            counts.set(label, (counts.get(label) ?? 0) + 1);
        return Array.from(counts, ([label, n]) => `${o.ctx.product.name} (${label}) x ${n}`).join(', ');
    }
    const variant = o.ctx.variant.name?.trim();
    return `${o.ctx.product.name}${variant ? ` (${variant})` : ''} x ${productQuantity(o)}`;
}
/** `{product_title}` — the same list without quantities, each variant once. */
function productTitle(o) {
    const lines = o.ctx.lineItems?.length ? activeLines(o) : [];
    if (lines.length) {
        return lines.map((li) => `${li.name}${li.variant ? ` (${li.variant})` : ''}`).join(', ');
    }
    const labels = offerVariantLabels(o);
    if (labels.length) {
        return Array.from(new Set(labels))
            .map((label) => `${o.ctx.product.name} (${label})`)
            .join(', ');
    }
    const variant = o.ctx.variant.name?.trim();
    return `${o.ctx.product.name}${variant ? ` (${variant})` : ''}`;
}
/** `{product_quantity}` — units ordered: every cart line, or the tier's quantity, or the stepper. */
function productQuantity(o) {
    const lines = o.ctx.lineItems?.length ? activeLines(o) : [];
    if (lines.length)
        return lines.reduce((sum, li) => sum + li.quantity, 0);
    return activeTier(o)?.quantity ?? Math.max(1, o.build.state.quantity);
}
/** Every thank-you shortcode, on top of the three `tokens()` handles. */
export function thankYouTokens(o, template) {
    if (!template)
        return '';
    const values = collectFields(o.config, o.build.state);
    const std = (key) => resolveStd(o, values, key);
    // The form collects first/last name separately; split a single `full_name` field for the older
    // configs that have one (first word first, the remainder last), as the legacy did.
    let firstName = std('first_name');
    let lastName = std('last_name');
    if (!firstName && !lastName) {
        const parts = std('full_name').split(/\s+/).filter(Boolean);
        firstName = parts[0] ?? '';
        lastName = parts.slice(1).join(' ');
    }
    const rate = o.config.shipping_rates.find((r) => r.id === o.build.state.selectedRateId);
    const submitted = o.build.state.submitted;
    return (tokens(o, template)
        // Only when the caller knows the URL — see `ProductContext.pageUrl`.
        .replace(/\{page_url\}/g, (t) => o.ctx.pageUrl ?? t)
        .replace(/\{products_summary_with_quantity\}/g, productsSummary(o))
        .replace(/\{product_title\}/g, productTitle(o))
        .replace(/\{product_quantity\}/g, String(productQuantity(o)))
        .replace(/\{full_name\}/g, [firstName, lastName].filter(Boolean).join(' '))
        .replace(/\{first_name\}/g, firstName)
        .replace(/\{last_name\}/g, lastName)
        .replace(/\{phone\}/g, std('phone'))
        .replace(/\{email\}/g, std('email'))
        .replace(/\{full_address\}/g, [std('address'), std('city'), std('province'), std('postal_code')]
        .filter(Boolean)
        .join(', '))
        // `{address2}` first: it is a distinct token, and reading it after `{address}` invites the
        // next person to write a looser pattern that eats the `2`.
        .replace(/\{address2\}/g, std('address_2'))
        .replace(/\{address\}/g, std('address'))
        .replace(/\{province\}/g, std('province'))
        .replace(/\{city\}/g, std('city'))
        .replace(/\{zip_code\}/g, std('postal_code'))
        // With configured rates, the buyer's selection; with the legacy single static option (no
        // rates), the one label they saw. Empty only when rates exist but none was eligible.
        .replace(/\{shipping_rate_name\}/g, rate?.name ?? (o.config.shipping_rates.length ? '' : o.config.shipping_method.option_label))
        .replace(/\{order_note\}/g, std('order_note'))
        // Both come from the created order, so both are empty until the POST succeeded — which is the
        // only state this text is ever built in.
        .replace(/\{order_number\}/g, submitted?.orderNumber != null ? String(submitted.orderNumber) : '')
        .replace(/\{order_id\}/g, submitted?.orderId != null ? String(submitted.orderId) : ''));
}
/**
 * The merchant's thank-you HTML, downgraded to markdown.
 *
 * ⚠️ **This is the fallback now, not the main path** — `thankYouTree` / `htmlTree.ts` rebuilds the
 * page as components and keeps its design. Reach for this only when that returns `null`: markup it
 * cannot read, or a merchant who typed prose rather than HTML.
 *
 * 🚨 **The NubeSDK host has no raw-HTML component, and its `markdown` one strips tags.** Measured
 * against the host bundle: `markdown` runs `.replace(/<[^>]+>/g, '')` over its content, so handing it
 * HTML renders the merchant's page as one run-on paragraph with the words in the right order and
 * nothing else — no headings, no bold, no line breaks. Converting first is what keeps that floor
 * readable. The legacy bundle keeps using `innerHTML` (it has a shadow root and a DOM), so on this
 * path the two differ in fidelity by exactly what the host's parser supports: bold, italic,
 * headings, lists, links, images, line breaks. Inline `style` attributes are lost here — which is
 * precisely the gap `htmlTree.ts` closes, and why it is tried first.
 *
 * Order matters: `<script>`/`<style>` bodies go first (they are content, not markup, and would
 * otherwise survive as text), links and images before the blanket tag strip that would eat their
 * attributes, and entities last so `&lt;b&gt;` cannot be re-read as a tag. Decoding is the parser's
 * `decodeEntities`, shared so the two paths cannot disagree about a `&times;` — this file used to
 * keep a six-entry copy of the table, with `&amp;` in the wrong position.
 */
export function htmlToMarkdown(html) {
    if (!html.trim())
        return '';
    let out = html
        .replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1>/gi, '')
        .replace(/<!--[\s\S]*?-->/g, '')
        .replace(/<a\b[^>]*\bhref\s*=\s*["']([^"']*)["'][^>]*>([\s\S]*?)<\/a>/gi, '[$2]($1)')
        .replace(/<img\b[^>]*\bsrc\s*=\s*["']([^"']*)["'][^>]*>/gi, (tag, src) => `![${/\balt\s*=\s*["']([^"']*)["']/i.exec(tag)?.[1] ?? ''}](${src})`)
        .replace(/<br\b[^>]*\/?>/gi, '\n')
        .replace(/<\/(p|div|h[1-6]|ul|ol|table|tr|blockquote)\s*>/gi, '\n\n')
        .replace(/<h([1-6])\b[^>]*>/gi, (_tag, level) => `\n\n${'#'.repeat(Number(level))} `)
        .replace(/<li\b[^>]*>/gi, '\n- ')
        .replace(/<\/li\s*>/gi, '')
        .replace(/<(strong|b)\b[^>]*>|<\/(strong|b)\s*>/gi, '**')
        .replace(/<(em|i)\b[^>]*>|<\/(em|i)\s*>/gi, '*')
        .replace(/<[^>]+>/g, '');
    out = decodeEntities(out);
    return out
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}
/** The merchant's page, interpolated and converted. Empty when they authored none. */
export function thankYouMarkdown(o, html) {
    return htmlToMarkdown(thankYouTokens(o, html));
}
/**
 * The merchant's page as a component tree — the preferred rendering, with the markdown above as the
 * fallback for whatever it cannot read.
 *
 * `htmlToTree` returns `null` rather than a degraded tree when the input is not markup it can
 * render, which is what keeps this strictly better than the old behaviour: the page either gains
 * its design back or lands on exactly the markdown it produced before.
 */
export function thankYouTree(o, html) {
    return htmlToTree(thankYouTokens(o, html));
}
//# sourceMappingURL=thankYou.js.map